import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Tooltip,
  LinearProgress,
  Fade,
  Paper,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { galleryImages } from '../galleryImages';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import {
  Package,
  DollarSign,
  ShoppingBag,
  Briefcase,
  Smartphone,
  Coffee,
  Edit,
  Upload
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AGTEnhanceButton from './AGTEnhanceButton';

// Styled Dialog sin sombra
const StyledDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiDialog-paper': {
      borderRadius: '28px',
      padding: theme.spacing(2),
      overflow: 'hidden',
      boxShadow: 'none',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
    }
  };
});

const StepContent = styled(DialogContent)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  transition: 'all 0.3s ease-in-out',
  '&.slide-enter': {
    transform: 'translateX(100%)',
    opacity: 0
  },
  '&.slide-enter-active': {
    transform: 'translateX(0)',
    opacity: 1
  },
  '&.slide-exit': {
    transform: 'translateX(-100%)',
    opacity: 0
  }
}));

const StepIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1)
}));

const StepDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'completed'
})(({ theme, active, completed }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: active
    ? (theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main)
    : completed
      ? (theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.6)' : theme.palette.primary.light)
      : (theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : theme.palette.grey[300]),
  transition: 'all 0.3s ease-in-out',
  transform: active ? 'scale(1.2)' : 'scale(1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.1)'
  }
}));

const NoShadowTooltip = styled(Tooltip)(() => ({
  '& .MuiTooltip-tooltip': {
    boxShadow: 'none'
  }
}));

// Componente para mostrar el resumen del producto
const SummaryContent = ({ steps, productData }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          color: (theme) => theme.palette.actualMode === 'wallpaper'
            ? '#FFFFFF'
            : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000')
        }}
      >
        {t('agtUps.productsManager.productSetupWizard.productSummary')}
      </Typography>
      <Grid container spacing={2}>
        {steps.map((step, index) => (
          <Grid item xs={12} key={index}>
            <Paper
              sx={{
                p: 2,
                borderRadius: '16px',
                boxShadow: 'none',
                backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(128, 128, 128, 0.2)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
                backdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                WebkitBackdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                border: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? '1px solid rgba(128, 128, 128, 0.3)'
                  : 'none'
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : theme.palette.primary.main
                }}
              >
                {t(`agtUps.productsManager.productSetupWizard.${step.field}.title`, step.title)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)')
                }}
              >
                {step.getValue(productData)}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Categorías por defecto
const defaultCategories = [
  { id: 'physical', name: 'Physical Products', icon: Package, color: '#4f46e5' },
  { id: 'digital', name: 'Digital Products', icon: Smartphone, color: '#0891b2' },
  { id: 'services', name: 'Services', icon: Briefcase, color: '#059669' },
  { id: 'subscription', name: 'Subscriptions', icon: ShoppingBag, color: '#7c3aed' },
  { id: 'food', name: 'Food & Beverage', icon: Coffee, color: '#db2777' }
];

// Agregamos un valor por defecto en el parámetro productData para evitar que sea undefined.
const ProductSetupWizard = ({
  open,
  onClose,
  productData = {
    name: '',
    description: '',
    category: '',
    price: '',
    stock: '',
    customFields: {},
    imageUrl: ''
  },
  onChange,
  onSubmit,
  onImageUpload,
  imagePreview,
  enhanceLoading,
  onEnhanceDescription,
  onEnhanceCategory,
  onEnhancePrice,
  onGetMarketingTips,
  onSelectGalleryImage,
  loading,
  isTyping
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priceEnhanced, setPriceEnhanced] = useState(false);
  const { t } = useTranslation();

  const handleEnhancePrice = useCallback(async () => {
    await onEnhancePrice();
    setPriceEnhanced(true);
  }, [onEnhancePrice]);

  // Construcción condicional de los pasos
  const steps = useMemo(() => {
    const baseSteps = [
      {
        title: t('agtUps.productsManager.productSetupWizard.productName.title'),
        description: t('agtUps.productsManager.productSetupWizard.productName.description'),
        required: true,
        field: 'name',
        getValue: (data) => data.name || t('agtUps.productsManager.productSetupWizard.notProvided'),
        component: (
          <TextField
            fullWidth
            label={t('agtUps.productsManager.productSetupWizard.productName.title')}
            name="name"
            value={productData.name}
            onChange={onChange}
            required
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '28px',
                color: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? '#FFFFFF'
                  : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : undefined
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : undefined
                }
              },
              '& .MuiInputLabel-root': {
                color: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.7)'
                  : undefined,
                '&.Mui-focused': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : undefined
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.3)'
                  : undefined
              }
            }}
          />
        )
      },
      {
        title: t('agtUps.productsManager.productSetupWizard.category.title'),
        description: t('agtUps.productsManager.productSetupWizard.category.description'),
        required: true,
        field: 'category',
        getValue: (data) => {
          const category = defaultCategories.find(c => c.id === data.category);
          return category ? category.name : t('agtUps.productsManager.productSetupWizard.notProvided');
        },
        component: (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                {t('agtUps.productsManager.productSetupWizard.category.title')}
              </InputLabel>
              <Select
                name="category"
                value={productData.category}
                onChange={onChange}
                sx={{
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : undefined
                  },
                  '& .MuiSvgIcon-root': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                {defaultCategories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.createElement(cat.icon, {
                        size: 16,
                        style: { color: cat.color }
                      })}
                      {cat.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mt: 2 }}>
              <AGTEnhanceButton
                onEnhance={onEnhanceCategory}
                isLoading={enhanceLoading.category}
                disabled={enhanceLoading.category}
                label={t('agtUps.productsManager.aiEnhance.category')}
                tooltip={t('agtUps.productsManager.aiEnhance.categoryTooltip')}
              />
            </Box>
          </Box>
        )
      }
    ];

    // Agregar el paso "Stock" solo para Physical Products o Food & Beverage
    if (productData.category === 'physical' || productData.category === 'food') {
      baseSteps.push({
        title: t('agtUps.productsManager.productSetupWizard.stock.title'),
        description: t('agtUps.productsManager.productSetupWizard.stock.description'),
        required: true,
        field: 'stock',
        getValue: (data) => data.stock || t('agtUps.productsManager.productSetupWizard.notProvided'),
        component: (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={t('agtUps.productsManager.productSetupWizard.stock.title')}
              name="stock"
              type="number"
              value={productData.stock}
              onChange={onChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
          </Box>
        )
      });
    }

    // Agregar los pasos restantes
    baseSteps.push(
      {
        title: t('agtUps.productsManager.productSetupWizard.description.title'),
        description: t('agtUps.productsManager.productSetupWizard.description.description'),
        required: true,
        field: 'description',
        getValue: (data) => data.description || t('agtUps.productsManager.productSetupWizard.notProvided'),
        component: (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('agtUps.productsManager.productSetupWizard.description.title')}
              name="description"
              value={productData.description}
              onChange={onChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
            <Box sx={{ mt: 2 }}>
              <AGTEnhanceButton
                onEnhance={onEnhanceDescription}
                isLoading={enhanceLoading.description}
                disabled={enhanceLoading.description || isTyping}
                label={t('agtUps.productsManager.aiEnhance.description')}
                tooltip={t('agtUps.productsManager.aiEnhance.descriptionTooltip')}
              />
            </Box>
          </Box>
        )
      },
      {
        title: t('agtUps.productsManager.productSetupWizard.price.title'),
        description: t('agtUps.productsManager.productSetupWizard.price.description'),
        required: true,
        field: 'price',
        getValue: (data) => data.price ? `$${data.price}` : t('agtUps.productsManager.productSetupWizard.notProvided'),
        component: (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={t('agtUps.productsManager.productSetupWizard.price.title')}
              name="price"
              type="number"
              value={productData.price}
              onChange={onChange}
              InputProps={{
                startAdornment: (
                  <DollarSign
                    size={16}
                    style={{
                      marginRight: 8,
                      color: 'inherit'
                    }}
                  />
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
            {priceEnhanced && (
              <Typography
                variant="caption"
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#007BFE',
                  mt: 1
                }}
              >
                {t('agtUps.productsManager.productSetupWizard.priceEnhanced')}
              </Typography>
            )}
            <Box sx={{ mt: 2 }}>
              <AGTEnhanceButton
                onEnhance={handleEnhancePrice}
                isLoading={enhanceLoading.price}
                disabled={enhanceLoading.price}
                label={t('agtUps.productsManager.aiEnhance.price')}
                tooltip={t('agtUps.productsManager.aiEnhance.priceTooltip')}
              />
            </Box>
          </Box>
        )
      },
      {
        title: t('agtUps.productsManager.productSetupWizard.image.title'),
        description: t('agtUps.productsManager.productSetupWizard.image.description'),
        required: false,
        field: 'imageUrl',
        getValue: (data) => data.imageUrl ? t('agtUps.productsManager.productSetupWizard.imageProvided') : t('agtUps.productsManager.productSetupWizard.noImage'),
        component: (
          <Box sx={{ mt: 2 }}>
            {/* Upload Section */}
            <Box
              sx={{
                border: '2px dashed',
                borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.3)'
                  : 'rgba(0,0,0,0.1)',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                mb: 4
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000')
                }}
              >
                {t('agtUps.productsManager.productSetupWizard.imageUpload.title')}
              </Typography>
              <input
                type="file"
                accept="image/png, image/jpeg"
                onChange={onImageUpload}
                style={{ display: 'none' }}
                id="product-image-upload"
              />
              <label htmlFor="product-image-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={imagePreview ? <Edit /> : <Upload />}
                  sx={{
                    borderRadius: '28px',
                    mb: 2,
                    color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(3,133,255,0.08)',
                      borderColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#E0E0E0' : '#0266cc'
                    }
                  }}
                >
                  {imagePreview
                    ? t('agtUps.productsManager.productSetupWizard.imageUpload.change')
                    : t('agtUps.productsManager.productSetupWizard.imageUpload.button')
                  }
                </Button>
              </label>
              {imagePreview && (
                <Box sx={{ mt: 2, position: 'relative', maxWidth: '200px', margin: '0 auto' }}>
                  <img
                    src={imagePreview}
                    alt="Product Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px'
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Gallery Section */}
            <Box sx={{ mt: 4 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  textAlign: 'center',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000')
                }}
              >
                {t('agtUps.productsManager.productSetupWizard.imageGallery.title')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: 2,
                  justifyContent: 'center'
                }}
              >
                {galleryImages.map((url, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: '100%',
                      aspectRatio: '1',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border:
                        productData.imageUrl === url
                          ? '3px solid #1DB954'
                          : '2px solid transparent',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.05)' }
                    }}
                    onClick={() => {
                      onChange({
                        target: { name: 'imageUrl', value: url }
                      });
                      if (typeof onSelectGalleryImage === 'function') {
                        onSelectGalleryImage(url);
                      }
                    }}
                  >
                    <img
                      src={url}
                      alt={`Gallery ${index}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )
      },
      {
        title: t('agtUps.productsManager.productSetupWizard.marketingTips.title'),
        description: t('agtUps.productsManager.productSetupWizard.marketingTips.description'),
        required: false,
        field: 'marketingTips',
        getValue: () => t('agtUps.productsManager.productSetupWizard.optionalMarketing'),
        component: (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.7)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)')
              }}
            >
              {t('agtUps.productsManager.productSetupWizard.marketingTips.description')}
            </Typography>
            <AGTEnhanceButton
              onEnhance={onGetMarketingTips}
              isLoading={loading}
              disabled={loading}
              label={t('agtUps.productsManager.productSetupWizard.marketingTips.button')}
              tooltip={t('agtUps.productsManager.productSetupWizard.marketingTips.tooltip')}
              sx={{ minWidth: '200px' }}
            />
          </Box>
        )
      }
    );

    return baseSteps;
  }, [
    productData,
    onChange,
    enhanceLoading,
    onEnhanceDescription,
    onEnhanceCategory,
    handleEnhancePrice,
    imagePreview,
    onImageUpload,
    isTyping,
    onGetMarketingTips,
    loading,
    priceEnhanced,
    onSelectGalleryImage,
    t
  ]);

  const validateStep = useCallback(
    (stepIndex) => {
      const step = steps[stepIndex];
      if (!step) return true; // Si el paso no existe, se retorna true (o false, según la lógica deseada)
      if (!step.required) return true;
      const value = productData[step.field];
      return value !== undefined && value !== '';
    },
    [steps, productData]
  );  

  const handleStepTransition = useCallback((nextStep) => {
    setTransitioning(true);
    const timeout = setTimeout(() => {
      setActiveStep(nextStep);
      setTransitioning(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, []);

  const handleNext = useCallback(async () => {
    if (!validateStep(activeStep)) return;
    if (activeStep === steps.length - 1) {
      if (!showSummary) {
        setShowSummary(true);
        return;
      }
      setIsSubmitting(true);
      try {
        await onSubmit();
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    handleStepTransition(activeStep + 1);
  }, [activeStep, steps.length, showSummary, onSubmit, validateStep, handleStepTransition]);

  const handleBack = useCallback(() => {
    if (showSummary) {
      setShowSummary(false);
      return;
    }
    handleStepTransition(activeStep - 1);
  }, [activeStep, showSummary, handleStepTransition]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext]);

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setShowSummary(false);
    }
  }, [open]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <Box sx={{ width: '100%', mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={((activeStep + (showSummary ? 1 : 0)) / steps.length) * 100}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(0,123,254,0.12)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#007BFE'
            }
          }}
        />
        <Typography
          variant="caption"
          align="center"
          sx={{
            display: 'block',
            mt: 0.5,
            color: (theme) => theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.7)'
              : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)')
          }}
        >
          {t('agtUps.productsManager.productSetupWizard.step', { current: activeStep + 1, total: steps.length })}
        </Typography>
      </Box>

      <StepIndicator>
        {steps.map((step, index) => (
          <NoShadowTooltip key={index} title={t(`agtUps.productsManager.productSetupWizard.${step.field}.title`, step.title)}>
            <StepDot
              active={index === activeStep}
              completed={index < activeStep}
              onClick={() => {
                if (index < activeStep) handleStepTransition(index);
              }}
            />
          </NoShadowTooltip>
        ))}
      </StepIndicator>

      <DialogTitle>
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: 600,
            color: (theme) => theme.palette.actualMode === 'wallpaper'
              ? '#FFFFFF'
              : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000')
          }}
        >
          {showSummary
            ? t('agtUps.productsManager.productSetupWizard.reviewTitle')
            : t(`agtUps.productsManager.productSetupWizard.${steps[activeStep].field}.title`, steps[activeStep].title)
          }
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 1,
            color: (theme) => theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.7)'
              : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)')
          }}
        >
          {showSummary
            ? t('agtUps.productsManager.productSetupWizard.reviewDescription')
            : t(`agtUps.productsManager.productSetupWizard.${steps[activeStep].field}.description`, steps[activeStep].description)
          }
        </Typography>
      </DialogTitle>

      <StepContent sx={{ opacity: transitioning ? 0 : 1 }}>
        {showSummary ? (
          <Suspense fallback={<Box sx={{ p: 3 }}>Loading summary...</Box>}>
            <SummaryContent steps={steps} productData={productData} />
          </Suspense>
        ) : (
          steps[activeStep].component
        )}
      </StepContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {(activeStep > 0 || showSummary) && (
          <Button
            onClick={handleBack}
            startIcon={<ArrowBack sx={{ color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#007BFE' }} />}
            sx={{
              textTransform: 'none',
              borderRadius: '28px',
              color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#007BFE',
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            {t('agtUps.productsManager.productSetupWizard.back')}
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          disableElevation
          onClick={handleNext}
          disabled={!validateStep(activeStep) || isSubmitting}
          endIcon={!showSummary && <ArrowForward />}
          sx={{
            textTransform: 'none',
            borderRadius: '30px',
            backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
            color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
            boxShadow: 'none',
            gap: (theme) => theme.spacing(1),
            padding: (theme) => theme.spacing(1, 2),
            '&:hover': {
              backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : '#0266cc',
              boxShadow: 'none'
            },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {isSubmitting
            ? t('agtUps.productsManager.productSetupWizard.creatingProduct')
            : showSummary
              ? t('agtUps.productsManager.productSetupWizard.createProduct')
              : activeStep === steps.length - 1
                ? t('agtUps.productsManager.productSetupWizard.reviewButton')
                : t('agtUps.productsManager.productSetupWizard.continue')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ProductSetupWizard;